import React, { useEffect, useMemo, useState, useRef } from 'react';
import { usePathname } from 'next/navigation';
import { cn } from '@/lib/utils';
import { getSiteBackgroundMode } from '@/lib/siteBackground';
import { createWidgetBackgroundStyles, getWidgetSettings, getParticleSettings, getHoverSettings } from '@/lib/widgetSettings';
import { useSiteTheme } from '@/components/SiteThemeProvider';
import { useSiteSettings } from '@/lib/swr/hooks/useSiteSettings';
import WidgetParticles from '@/components/WidgetParticles';

interface CardProps {
  children: React.ReactNode;
  className?: string;
  hover?: boolean;
  variant?: 'default' | 'gradient' | 'glass';
  onClick?: React.MouseEventHandler<HTMLDivElement>; // для кликабельных карточек
  onDoubleClick?: React.MouseEventHandler<HTMLDivElement>; // для двойного клика
  onContextMenu?: React.MouseEventHandler<HTMLDivElement>;
  useWidgetSettings?: boolean; // использовать глобальные настройки виджетов
  style?: React.CSSProperties; // дополнительные стили
  enableParticles?: boolean; // включить эффекты частиц (по умолчанию true если включены в настройках)
}

export default function Card({
  children,
  className = '',
  hover = false,
  variant = 'default',
  onClick,
  onDoubleClick,
  onContextMenu,
  useWidgetSettings = false,
  style,
  enableParticles
}: CardProps) {
  const backgroundMode = getSiteBackgroundMode(usePathname() || '');
  const { tokens } = useSiteTheme();
  const { settings: sharedSettings } = useSiteSettings();
  const settings = useWidgetSettings ? sharedSettings : null;
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });
  const [isHovered, setIsHovered] = useState(false);
  const cardRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (cardRef.current && settings) {
      const rect = cardRef.current.getBoundingClientRect();
      setDimensions({ width: rect.width, height: rect.height });
    }
  }, [settings]);

  const widgetSettings = useMemo(() => (settings ? getWidgetSettings(settings) : null), [settings]);
  const hoverSettings = useMemo(() => (settings ? getHoverSettings(settings) : null), [settings]);
  const particleSettings = useMemo(() => (settings ? getParticleSettings(settings) : null), [settings]);

  // Если включены настройки виджетов и они загружены
  if (useWidgetSettings && settings && widgetSettings && hoverSettings && particleSettings) {
    const baseStyle = createWidgetBackgroundStyles(settings);
    const { backgroundColor: _ignored, ...restBaseStyle } = baseStyle;

    const baseBackground = `rgb(var(--site-surface-rgb) / ${widgetSettings.opacity})`;
    const hoverBackground = `rgb(var(--site-raised-rgb) / ${hoverSettings.opacity})`;
    const baseBorderColor = 'var(--site-border)';
    const hoverBorderColor = 'var(--site-border-control)';

    const dynamicStyles: React.CSSProperties = {
      ...restBaseStyle,
      backgroundColor: hover && isHovered ? hoverBackground : baseBackground,
      borderColor: hover && isHovered ? hoverBorderColor : baseBorderColor,
      ...style,
      transform: hover ? (isHovered ? `scale(${hoverSettings.scale})` : 'scale(1)') : undefined,
      cursor: onClick ? 'pointer' : style?.cursor
    };

    const hoverClasses = hover ? 'hover:shadow-xl' : '';

    const backgroundBlurClass = widgetSettings.backgroundBlur ? 'backdrop-blur-sm' : '';

    const shouldShowParticles =
      backgroundMode === 'featured' && enableParticles !== false && particleSettings.enabled && dimensions.width > 0 && dimensions.height > 0;

    return (
      <div
        ref={cardRef}
        className={cn('transition-all relative overflow-hidden', backgroundBlurClass, hoverClasses, className)}
        style={dynamicStyles}
        onClick={onClick}
        onDoubleClick={onDoubleClick}
        onContextMenu={onContextMenu}
        onMouseEnter={() => hover && setIsHovered(true)}
        onMouseLeave={() => hover && setIsHovered(false)}
      >
        {children}
        {shouldShowParticles && (
          <WidgetParticles
            settings={{ ...particleSettings, color: tokens.particleColor }}
            width={dimensions.width}
            height={dimensions.height}
          />
        )}
      </div>
    );
  }

  // Оригинальная логика для обратной совместимости
  const baseClasses = 'rounded-xl transition-all duration-300';

  const variantClasses = {
    default: 'bg-site-surface/50 border border-site-border backdrop-blur-sm',
    gradient: 'bg-gradient-to-br from-site-surface/60 to-site-bg/60 border border-site-border-control/50 backdrop-blur-sm',
    glass: 'glass'
  };

  const hoverClasses = hover ? 'hover:shadow-xl' : '';

  return (
    <div
      className={cn(baseClasses, variantClasses[variant], hoverClasses, onClick && 'cursor-pointer', className)}
      style={style}
      onClick={onClick}
      onDoubleClick={onDoubleClick}
      onContextMenu={onContextMenu}
    >
      {children}
    </div>
  );
}
